import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import React from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Layout from '@/layouts';
import InfluencersDetailContent from '@/components/InfluencersContent/InfluencersDetailContent';
import CustomHead from '@/components/Common/CustomHead';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page3JsonLd } from '@/lib/jsonld';
import { fetchHairstyleBloggerInfoData, fetchHairstyleBloggerInfoPageData } from '@/lib/service';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl, query } = context;
  const { name } = query;

  const localeRes = await serverSideTranslations(locale, ['common']);
  const tdkData = await getTdkApi(resolvedUrl, locale);

  // 在服务端获取博主信息
  const bloggerInfoResponse = await fetchHairstyleBloggerInfoData(`${name}`, locale);
  const bloggerInfo = bloggerInfoResponse?.code === 0 ? bloggerInfoResponse.data : null;

  // 在服务端获取博主的发型列表
  const params = {
    page_no: 1,
    page_size: 30
  };

  const hairstyleListResponse = await fetchHairstyleBloggerInfoPageData(
    `${name}`,
    params.page_no,
    params.page_size,
    null,
    locale
  );

  const hairstyleList = hairstyleListResponse?.code === 0 ? hairstyleListResponse : null;

  const title = `${name}'s Top Hairstyles & Trends | Hairpaca`;
  const desc = `Discover ${name}'s latest hairstyles and trends on Hairpaca. Get inspired by unique styles and try on your favorite looks virtually.`;
  const jsonLd = page3JsonLd(
    {
      title: tdkData?.data?.title ?? title,
      desc: tdkData?.data?.description ?? desc,
      url: canonicalUrl(req.headers.host, resolvedUrl, locale)
    },
    {
      title: 'Influencers',
      url: canonicalUrl(req.headers.host, '/influencers', locale)
    },
    {
      url: canonicalUrl(req.headers.host, '/', locale)
    }
  );
  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkData?.data?.title ?? title,
      description: tdkData?.data?.description ?? desc,
      jsonLd,
      // 传递初始数据
      initialBloggerInfo: bloggerInfo || null,
      initialHairstyleList: hairstyleList?.data || [],
      initialHasNext: hairstyleList?.has_next || false
    }
  };
};

const Index: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <InfluencersDetailContent data={props} />;
};

Index.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title ?? '';
  const desc = page?.props?.description ?? '';

  return (
    <Layout
      head={
        <CustomHead>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </CustomHead>
      }
    >
      {page}
    </Layout>
  );
};

export default Index;
